import { API_ENDPOINTS } from '../../config/api';

class AdminService {
    // Headers for HTTP APIs (JWT authorizer - no Bearer prefix)
    get headers() {
        const token = localStorage.getItem('adminToken');
        return {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': token } : {})
        };
    }

    // Headers for REST APIs (Cognito User Pool authorizer - needs Bearer prefix)
    get headersWithBearer() {
        const token = localStorage.getItem('adminToken');
        return {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };
    }

    // --- Customer Management ---

    async getCustomers() {
        try {
            const response = await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
                method: 'GET',
                headers: this.headersWithBearer
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching customers:', error);
            throw error;
        }
    }

    async getDashboardStats() {
        try {
            // For now, we reuse the customers endpoint which returns stats object
            // In future, this should point to a dedicated /admin/stats endpoint
            const response = await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
                method: 'GET',
                headers: this.headersWithBearer
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            return {
                stats: data.stats || {},
                recentCustomers: data.customers ? data.customers.slice(0, 5) : []
            };
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            // Return empty structure to prevent UI crash
            return { stats: {}, recentCustomers: [] };
        }
    }

    async getCustomerActionHistory(customerId, customerEmail, limit = 100) {
        try {
            const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
                method: 'POST',
                headers: this.headers,
                body: JSON.stringify({
                    action: 'getActionHistory',
                    customerId,
                    customerEmail,
                    email: customerEmail,
                    limit
                })
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await response.json();
        } catch (error) {
            console.error('Error fetching action history:', error);
            throw error;
        }
    }

    // Proof-of-address documents are no longer inlined in the customer list —
    // they live in a private S3 bucket. This returns a presigned URL valid for
    // 5 minutes. Fetch it at the moment the admin clicks, never ahead of time.
    async getProofOfAddressUrl(customerUID) {
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({ action: 'getProofOfAddressUrl', customerId: customerUID })
        });
        const data = await response.json();
        if (!response.ok || !data.url) {
            throw new Error(data.error || `Could not fetch document (HTTP ${response.status})`);
        }
        return data;
    }

    async approveCustomer(payload) {
        // payload: { customerId, customerEmail, approvalNotes, billingCycleStart, serviceActivationDate, approvedBy }
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'approveCustomer',
                email: payload.customerEmail, // Legacy support
                ...payload,
                sendEmail: true,
                adminUser: payload.approvedBy || 'admin-portal'
            })
        });
        return response.json();
    }

    async rejectCustomer(payload) {
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'rejectCustomer',
                email: payload.customerEmail,
                ...payload,
                // After spread so we honour caller's explicit choice when set,
                // and fall back to true only when caller didn't pass sendEmail.
                sendEmail: payload.sendEmail !== undefined ? payload.sendEmail : true,
                adminUser: payload.adminUser || 'admin-portal'
            })
        });
        return response.json();
    }

    async provisionCustomer(payload) {
        // payload: { customerId, customerEmail, planId?, vocusSpeedTier?, planMapping?, password?, username?, adminUser? }
        // Backend (lambda-admin-customer-management.js handleProvisionCustomer)
        //  - validates vocusSqSnapshot has a LOC
        //  - resolves PlanID from (Plans.vocusSpeedTier × QUALIFY.accessTechnology × planMapping)
        //  - invokes check-vocus-availability mode=CREATE with resolved PlanID + generated PPPoE password
        //  - stores vocusOrderSnapshot + provisionStatus='pending' (or 'inactive' on fault)
        //  - returns the snapshot including resolvedPlanId, pppoeUsername (password is on customer record)
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'provision',
                email: payload.customerEmail,
                ...payload,
                adminUser: payload.adminUser || 'admin-portal'
            })
        });
        return response.json();
    }

    async withdrawProvision(payload) {
        // payload: { customerId, customerEmail, transactionId?, adminUser? }
        // Vocus enforces a 3-business-day window before WITHDRAW is allowed via FIBRE::Delete
        // Scope=PROVISIONING. Errors with FIBRE-2205 if attempted earlier.
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'withdrawProvision',
                email: payload.customerEmail,
                ...payload,
                adminUser: payload.adminUser || 'admin-portal'
            })
        });
        return response.json();
    }

    async getProvisionStatus(payload) {
        // payload: { customerId, customerEmail }
        // Read-only — returns the snapshot stored in DDB. Use this when entering the modal
        // to get the latest provisionStatus + vocusOrderSnapshot without forcing a Vocus poll.
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'getProvisionStatus',
                email: payload.customerEmail,
                ...payload,
            })
        });
        return response.json();
    }

    async getCustomerConsents(payload) {
        // payload: { customerId }
        // Returns the full legal-consent audit trail for a customer — every signup-time
        // and admin-recorded acknowledgement, newest first. Used by ConsentsSection.
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'getCustomerConsents',
                ...payload,
            })
        });
        return response.json();
    }

    async markProvisioned(payload) {
        // payload: { customerId (UID), provisionMethod: 'auto'|'manual', vocusServiceId? }
        // Records HOW a customer's NBN service was provisioned (auto via admin portal, or manual
        // via the Vocus portal). Sets provisionStatus='active' so they stop showing "not
        // provisioned". Does NOT submit any Vocus order.
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'markProvisioned',
                ...payload,
            })
        });
        return response.json();
    }

    async recordAdminConsent(payload) {
        // payload: { customerId, docKey, version, method?, note?, adminUser? }
        // Records an offline consent (voice, in-person, email confirmation) into the
        // CustomerConsents audit table. Method defaults to 'admin-recorded'.
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'recordAdminConsent',
                ...payload,
                adminUser: payload.adminUser || 'admin-portal',
            })
        });
        return response.json();
    }

    async cancelVocusService(payload) {
        // payload: { customerId, customerEmail, serviceId?, planId?, force?, adminUser? }
        // Deactivates an ACTIVE Vocus FIBRE service via FIBRE::Set Scope=STATUS, ServiceStatus=INACTIVE.
        // For SUCCESS-state orders only — for QUEUED/PROCESSING use withdrawProvision instead.
        // Stops billing on the AVC. Sets provisionStatus='cancelled'.
        // (Note: this is a Vocus-side deactivation, NOT the Peaknet account `cancelService` action.)
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'cancelVocusService',
                email: payload.customerEmail,
                ...payload,
                adminUser: payload.adminUser || 'admin-portal'
            })
        });
        return response.json();
    }

    async checkVocusStatus(payload) {
        // payload: { customerId, customerEmail, transactionId? }
        // Live poll — invokes Vocus TCAS::Get RESPONSE and applies state transitions
        // (QUEUED → PROCESSING → SUCCESS/FAILED/WITHDRAWN). Mutates provisionStatus.
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'checkVocusStatus',
                email: payload.customerEmail,
                ...payload,
            })
        });
        return response.json();
    }

    async putOnHold(payload) {
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'putOnHold',
                email: payload.customerEmail,
                ...payload,
                sendEmail: true,
                adminUser: payload.adminUser || 'admin-portal'
            })
        });
        return response.json();
    }

    async updateServiceActivationDate(payload) {
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'updateServiceActivation',
                email: payload.customerEmail,
                ...payload
            })
        });
        return response.json();
    }

    async updateCustomer(payload) {
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'update',
                email: payload.customerEmail || payload.email,
                ...payload
            })
        });
        return response.json();
    }

    // --- Plan Management / Pricing ---

    async getCustomerPricing(customerId, customerEmail) {
        const response = await fetch(API_ENDPOINTS.PLAN_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'getCustomerPricing',
                customerId,
                customerEmail
            })
        });
        return response.json();
    }

    async setCustomerBasePricing(payload) {
        const response = await fetch(API_ENDPOINTS.PLAN_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'setCustomerBasePricing',
                email: payload.customerEmail,
                ...payload
            })
        });
        return response.json();
    }

    async setCustomerPricingOverride(payload) {
        const response = await fetch(API_ENDPOINTS.PLAN_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'setCustomerPricingOverride',
                email: payload.customerEmail,
                ...payload
            })
        });
        return response.json();
    }

    async clearCustomerPricingOverride(customerId, customerEmail) {
        const response = await fetch(API_ENDPOINTS.PLAN_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'clearCustomerPricingOverride',
                customerId,
                customerEmail,
                email: customerEmail,
                updatedBy: 'admin-portal'
            })
        });
        return response.json();
    }

    async setCustomerBillingDate(payload) {
        const response = await fetch(API_ENDPOINTS.PLAN_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'setCustomerBillingDate',
                email: payload.customerEmail,
                ...payload
            })
        });
        return response.json();
    }

    // --- Email ---

    async sendPasswordReset(email) {
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'sendPasswordReset',
                email
            })
        });
        return response.json();
    }

    async sendWelcomeEmail(emailData) {
        const response = await fetch(API_ENDPOINTS.SEND_WELCOME_EMAIL, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(emailData)
        });
        return response.json();
    }

    async assignCpe(payload) {
        // payload: { customerId (UID), serial, model?, mac? } — or { customerId, unassign: true }
        // Links a router (by serial) to a customer for TR-069 zero-touch provisioning.
        // Backend generates + stores Wi-Fi SSID/password. See docs/GENIEACS-SETUP.md.
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'assignCpe',
                ...payload,
            })
        });
        return response.json();
    }

    async cpeDevice(payload) {
        // payload: { customerId (UID), op: 'status'|'reboot'|'refresh' }
        // Live router operations, proxied through the backend to the GenieACS NBI
        // (the ACS API token never reaches the browser).
        const response = await fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({
                action: 'cpeDevice',
                ...payload,
            })
        });
        return response.json();
    }
}

export const adminService = new AdminService();
